import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { PackageIcon, TruckIcon, ChevronRightIcon, ChevronDownIcon, ExternalLinkIcon, ContainerIcon, PlusIcon, PrinterIcon, EditIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import ClienteForm from "@/components/ClienteForm";
import CargaEditModal from "@/components/CargaEditModal";

export default function Expedicao() {
  const { user } = useAuth();
  const canManageClientes = user?.role === "admin" || user?.role === "manager";
  const [cheios, setCheios] = useState([]);
  const [transito, setTransito] = useState([]);
  const [concluidos, setConcluidos] = useState([]);
  const [selected, setSelected] = useState({});
  const [form, setForm] = useState({ no_pedido: "", nf_produto: "", cliente: "", motorista: "", cpf_motorista: "", placa: "" });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [expandedHist, setExpandedHist] = useState({});
  const [editCarga, setEditCarga] = useState(null);
  const canEditCarga = user?.role === "admin" || user?.role === "manager";

  const load = async () => {
    const [c, t, no] = await Promise.all([
      api.get("/containers", { params: { status: "CHEIO" } }),
      api.get("/containers", { params: { status: "EM_TRANSITO" } }),
      api.get("/containers", { params: { status: "NO_CLIENTE" } }),
    ]);
    setCheios(c.data);
    setTransito(t.data);
    setConcluidos(no.data);
  };
  useEffect(() => { load(); }, []);

  const toggleSelect = (no_serie) =>
    setSelected((s) => {
      const was = !!s[no_serie];
      if (!was) {
        const n = Object.values(s).filter(Boolean).length;
        if (n >= 30) {
          toast.warning("Atenção: capacidade máxima por carreta é de 30 itens (tanques + refis).");
          return s;
        }
      }
      return { ...s, [no_serie]: !was };
    });

  const selectedList = Object.keys(selected).filter((k) => selected[k]);
  const selCount = selectedList.length;
  const dispatchColor =
    selCount === 0 ? "bg-muted text-muted-foreground"
    : selCount <= 15 ? "bg-[#00E5FF] text-black hover:bg-[#00E5FF]/85"
    : selCount <= 25 ? "bg-[#FFD60A] text-black hover:bg-[#ffdf3a]"
    : "bg-[#FF9F0A] text-black hover:bg-[#FF9F0A]/85";

  const [clientes, setClientes] = useState([]);
  const [openCliModal, setOpenCliModal] = useState(false);
  const loadClientes = () => api.get("/clientes", { params: { tipo: "destino" } }).then(({ data }) => setClientes(data.filter((c) => c.active !== false))).catch(() => {});
  useEffect(() => { loadClientes(); }, []);

  // Group transito by carga key
  const cargas = useMemo(() => {
    const groups = {};
    transito.forEach((t) => {
      const key = `${t.placa || "?"}|${t.motorista || "?"}|${t.no_pedido || "?"}`;
      if (!groups[key]) {
        groups[key] = {
          key, placa: t.placa, motorista: t.motorista, cpf_motorista: t.cpf_motorista,
          cliente: t.cliente, no_pedido: t.no_pedido, nf_produto: t.nf_produto,
          data_saida: t.data_saida, contentores: [],
        };
      }
      groups[key].contentores.push(t);
    });
    return Object.values(groups).sort((a, b) => (b.data_saida || "").localeCompare(a.data_saida || ""));
  }, [transito]);

  // Group concluidos (NO_CLIENTE) by carga key — sort by last delivery timestamp desc
  const historico = useMemo(() => {
    const groups = {};
    concluidos.forEach((t) => {
      const key = `${t.placa || "?"}|${t.motorista || "?"}|${t.no_pedido || "?"}`;
      if (!groups[key]) {
        groups[key] = {
          key, placa: t.placa, motorista: t.motorista, cpf_motorista: t.cpf_motorista,
          cliente: t.cliente, no_pedido: t.no_pedido, nf_produto: t.nf_produto,
          data_saida: t.data_saida, ultima_entrega: t.data_entrega_cliente,
          contentores: [],
        };
      }
      const g = groups[key];
      g.contentores.push(t);
      if ((t.data_entrega_cliente || "") > (g.ultima_entrega || "")) {
        g.ultima_entrega = t.data_entrega_cliente;
      }
    });
    return Object.values(groups).sort((a, b) => (b.ultima_entrega || "").localeCompare(a.ultima_entrega || ""));
  }, [concluidos]);

  const despachar = async (e) => {
    e?.preventDefault();
    if (selectedList.length === 0) return toast.error("Selecione ao menos um contentor");
    if (!form.cliente || !form.no_pedido || !form.nf_produto || !form.motorista || !form.cpf_motorista || !form.placa) {
      return toast.error("Preencha todos os campos do manifesto");
    }
    setLoading(true);
    try {
      for (const no_serie of selectedList) {
        await api.post("/expedicao", { ...form, no_serie });
      }
      toast.success(`Carreta despachada · ${selectedList.length} contentor(es) → EM TRÂNSITO`);
      setSelected({});
      setForm({ no_pedido: "", nf_produto: "", cliente: "", motorista: "", cpf_motorista: "", placa: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1700px] mx-auto">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mb-1">Tela 05 · Expedição</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-none">Manifesto de Expedição por Carreta</h1>
          <p className="text-sm text-muted-foreground mt-2">Selecione múltiplos contentores cheios e despache a carga inteira de uma vez (até 30 itens por carreta — tanques + refis).</p>
        </div>
        <PackageIcon className="w-12 h-12 text-[#00E5FF]" strokeWidth={1.2} />
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Lista esquerda — múltipla seleção */}
        <Card className="lg:col-span-2 bg-[#0A0A0C] border-border rounded-none p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-[#14151A] flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#FFD60A]">Contentores cheios · prontos</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">Marque os checkboxes para compor a carga</div>
            </div>
            <Badge className="rounded-none border bg-[#FFD60A]/15 text-[#FFD60A] border-[#FFD60A]/40 font-mono">{cheios.length}</Badge>
          </div>
          <div className="max-h-[560px] overflow-y-auto">
            {cheios.length === 0 && <div className="p-6 text-sm text-muted-foreground">Nenhum tanque cheio.</div>}
            {cheios.map((c) => {
              const sel = !!selected[c.no_serie];
              return (
                <label
                  key={c.no_serie}
                  className={`flex gap-3 items-start border-b border-border/60 px-4 py-3 cursor-pointer hover:bg-[#14151A] ${sel ? "bg-[#00E5FF]/5 border-l-2 border-l-[#00E5FF]" : "border-l-2 border-l-transparent"}`}
                  data-testid={`exp-cheio-${c.no_serie}`}
                >
                  <Checkbox
                    checked={sel}
                    onCheckedChange={() => toggleSelect(c.no_serie)}
                    data-testid={`exp-check-${c.no_serie}`}
                    className="mt-0.5 rounded-none border-border data-[state=checked]:bg-[#00E5FF] data-[state=checked]:text-black"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[#FFD60A] font-bold">{c.no_serie}</span>
                      <span className="text-xs font-mono text-muted-foreground">{c.capacidade}L</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate">{c.nome_produto}</div>
                    <div className="text-[11px] font-mono text-[#00E5FF] mt-0.5">Lote {c.no_lote} · val. {c.data_validade_lote}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </Card>

        {/* Painel direito — manifesto */}
        <Card className="lg:col-span-3 bg-[#14151A] border-border rounded-none p-6">
          <div className="flex items-center gap-2 mb-4">
            <TruckIcon className="w-5 h-5 text-[#00E5FF]" />
            <h2 className="font-display text-2xl font-bold">Manifesto de carga</h2>
          </div>

          {/* Contentores Selecionados */}
          <div className="mb-4 border border-border bg-[#0A0A0C] p-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Contentores Selecionados</Label>
              <Badge className="rounded-none border bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/40 font-mono">{selCount} / 30</Badge>
            </div>
            {selCount === 0 ? (
              <div className="text-sm text-muted-foreground py-2 flex items-center gap-2">
                <ContainerIcon className="w-4 h-4" /> Marque tanques na lista à esquerda
              </div>
            ) : (
              <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto" data-testid="exp-selected-list">
                {selectedList.map((s) => {
                  const c = cheios.find((x) => x.no_serie === s);
                  return (
                    <button
                      key={s}
                      onClick={() => toggleSelect(s)}
                      className="font-mono text-xs px-2 py-1 bg-[#FFD60A]/15 text-[#FFD60A] border border-[#FFD60A]/40 hover:bg-[#FFD60A]/25"
                      title={c?.nome_produto || ""}
                    >
                      {s} ×
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <form onSubmit={despachar} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 text-[11px] uppercase tracking-[0.3em] text-[#00E5FF]">Dados do Cliente e Carga</div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Cliente de Destino</Label>
              <div className="mt-1 flex gap-2">
                <select
                  data-testid="exp-cliente"
                  value={form.cliente}
                  onChange={(e) => setForm({ ...form, cliente: e.target.value })}
                  required
                  className="flex-1 rounded-none bg-[#0A0A0C] border border-border h-11 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#00E5FF]"
                >
                  <option value="">— selecione um cliente cadastrado —</option>
                  {clientes.map((cli) => (
                    <option key={cli.id} value={cli.nome}>{cli.nome} {cli.cnpj ? `· ${cli.cnpj}` : ""}{cli.cidade ? ` · ${cli.cidade}/${cli.estado || ""}` : ""}</option>
                  ))}
                </select>
                {canManageClientes && (
                  <Button
                    type="button"
                    onClick={() => setOpenCliModal(true)}
                    data-testid="exp-novo-cliente"
                    className="rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 h-11 px-3 font-bold uppercase tracking-wider text-xs"
                    title="Cadastrar novo cliente (gestor/admin)"
                  >
                    <PlusIcon className="w-4 h-4 mr-1" /> Novo
                  </Button>
                )}
              </div>
            </div>
            <Field label="N° do Pedido" testId="exp-pedido" value={form.no_pedido} onChange={(v) => setForm({ ...form, no_pedido: v })} mono />
            <Field label="NF do Produto" testId="exp-nf" value={form.nf_produto} onChange={(v) => setForm({ ...form, nf_produto: v })} mono />

            <div className="md:col-span-2 text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mt-2">Dados do Transporte</div>
            <Field label="Nome do Motorista" testId="exp-motorista" value={form.motorista} onChange={(v) => setForm({ ...form, motorista: v })} />
            <Field label="CPF do Motorista" testId="exp-cpf" value={form.cpf_motorista} onChange={(v) => setForm({ ...form, cpf_motorista: v })} mono />
            <Field label="Placa do Caminhão / Carreta" testId="exp-placa" value={form.placa} onChange={(v) => setForm({ ...form, placa: v.toUpperCase() })} mono full />

            <div className="md:col-span-2 flex justify-end pt-2">
              <Button
                type="submit"
                disabled={loading || selCount === 0}
                data-testid="exp-submit-carreta"
                className={`rounded-none h-14 px-10 font-bold uppercase tracking-wider text-base transition-colors ${dispatchColor}`}
              >
                <TruckIcon className="w-5 h-5 mr-2" />
                {loading ? "Despachando..." : `Despachar Carreta / Carga (${selCount})`}
              </Button>
            </div>
          </form>
        </Card>
      </div>

      {/* Em trânsito agrupado por carga */}
      <Card className="mt-8 bg-[#14151A] border-border rounded-none p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="font-display text-2xl font-bold">Em trânsito agora · por carga</h2>
            <div className="text-xs text-muted-foreground">{cargas.length} viagens · {transito.length} contentores</div>
          </div>
          <Badge className="rounded-none border bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/40">{transito.length}</Badge>
        </div>
        {cargas.length === 0 && <div className="p-8 text-center text-muted-foreground">Nenhum despacho ativo.</div>}
        <div className="divide-y divide-border/60">
          {cargas.map((carga) => {
            const isOpen = !!expanded[carga.key];
            return (
              <div key={carga.key} data-testid={`carga-${carga.placa}`}>
                <button
                  onClick={() => setExpanded((e) => ({ ...e, [carga.key]: !isOpen }))}
                  className="w-full grid grid-cols-12 gap-2 px-6 py-4 hover:bg-[#1C1D24] text-left items-center"
                  data-testid={`carga-toggle-${carga.placa}`}
                >
                  <div className="col-span-1">
                    {isOpen ? <ChevronDownIcon className="w-5 h-5 text-[#00E5FF]" /> : <ChevronRightIcon className="w-5 h-5 text-muted-foreground" />}
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Placa</div>
                    <div className="font-mono text-lg font-bold text-[#FFD60A]">{carga.placa}</div>
                  </div>
                  <div className="col-span-3">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Motorista</div>
                    <div className="text-sm">{carga.motorista}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{carga.cpf_motorista}</div>
                  </div>
                  <div className="col-span-3">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cliente</div>
                    <div className="text-sm">{carga.cliente}</div>
                    <div className="text-[10px] font-mono text-[#00E5FF]">Pedido {carga.no_pedido} · NF {carga.nf_produto}</div>
                  </div>
                  <div className="col-span-1 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Contentores</div>
                    <div className="font-display text-2xl font-bold text-[#00E5FF]">{carga.contentores.length}</div>
                  </div>
                  <div className="col-span-2 text-right">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Saída</div>
                    <div className="text-xs font-mono">{carga.data_saida ? new Date(carga.data_saida).toLocaleString("pt-BR") : "—"}</div>
                  </div>
                </button>
                {isOpen && (
                  <div className="bg-[#0A0A0C] border-t border-border/60 px-6 py-3" data-testid={`carga-detalhe-${carga.placa}`}>
                    <div className="flex justify-end mb-2 gap-3">
                      {canEditCarga && (
                        <button
                          onClick={() => setEditCarga(carga)}
                          data-testid={`editar-carga-${carga.placa}`}
                          className="text-[10px] uppercase tracking-widest text-[#FFD60A] hover:underline flex items-center gap-1"
                          title="Editar dados da carga inteira (cliente, pedido, NF, motorista, placa)"
                        >
                          <EditIcon className="w-3 h-3" /> Editar carga
                        </button>
                      )}
                      <a
                        href={`/romaneio/expedicao/${encodeURIComponent(carga.placa)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`imprimir-romaneio-${carga.placa}`}
                        className="text-[10px] uppercase tracking-widest text-[#FFD60A] hover:underline flex items-center gap-1"
                      >
                        <PrinterIcon className="w-3 h-3" /> Imprimir Romaneio
                      </a>
                    </div>
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        <tr>
                          <th className="text-left py-2">N° Série</th>
                          <th className="text-left py-2">Tipo</th>
                          <th className="text-left py-2">Lote</th>
                          <th className="text-right py-2">Litragem</th>
                          <th className="text-left py-2">GPS</th>
                          <th className="text-right py-2">Posição</th>
                          <th className="text-right py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {carga.contentores.map((c) => (
                          <tr key={c.no_serie} className="border-t border-border/30">
                            <td className="py-2 font-mono text-[#FFD60A]">{c.no_serie}</td>
                            <td className="py-2">{c.tipo_armazenamento} · {c.capacidade}L</td>
                            <td className="py-2 font-mono">{c.no_lote}</td>
                            <td className="py-2 text-right font-mono">{c.qtd_envasado} L</td>
                            <td className="py-2 font-mono">
                              <a
                                href={`https://www.openstreetmap.org/?mlat=${c.gps_lat}&mlon=${c.gps_lng}#map=15/${c.gps_lat}/${c.gps_lng}`}
                                target="_blank" rel="noopener noreferrer"
                                className="text-[#00E5FF] hover:underline flex items-center gap-1"
                              >
                                {c.gps_id} <ExternalLinkIcon className="w-3 h-3" />
                              </a>
                            </td>
                            <td className="py-2 text-right font-mono text-muted-foreground">{c.gps_lat?.toFixed(4)}, {c.gps_lng?.toFixed(4)}</td>
                            <td className="py-2 text-right whitespace-nowrap">
                              <Button
                                size="sm"
                                onClick={async () => {
                                  if (!window.confirm(`Confirmar entrega de ${c.no_serie} no cliente?`)) return;
                                  try {
                                    const r = await api.post(`/expedicao/${c.no_serie}/concluir`);
                                    toast.success(`Entrega registrada às ${new Date(r.data.data_entrega_cliente).toLocaleString("pt-BR")}`);
                                    load();
                                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                                }}
                                data-testid={`concluir-exp-${c.no_serie}`}
                                className="h-7 rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 font-bold uppercase tracking-wider text-[10px] px-3"
                              >
                                Concluir
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Histórico de Envios Concluídos */}
      <Card className="bg-[#14151A] border-border rounded-none p-0 overflow-hidden mt-4" data-testid="historico-envios">
        <div className="px-6 py-4 border-b border-border flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-[#34C759]">
            <PackageIcon className="w-4 h-4 text-black" strokeWidth={2.5} />
          </div>
          <h2 className="font-display text-2xl font-bold">Histórico de Envios Concluídos</h2>
          <Badge className="rounded-none border bg-[#34C759]/15 text-[#34C759] border-[#34C759]/40">{concluidos.length}</Badge>
          <div className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground">Cargas 100% entregues</div>
        </div>
        {historico.length === 0 && <div className="p-8 text-center text-muted-foreground">Nenhuma entrega concluída ainda. Ao clicar em Concluir nos itens de uma carga em trânsito, ela aparece aqui.</div>}
        <div className="divide-y divide-border/60">
          {historico.map((carga) => {
            const isOpen = !!expandedHist[carga.key];
            return (
              <div key={carga.key} data-testid={`carga-concluida-${carga.placa}`}>
                <button
                  onClick={() => setExpandedHist((e) => ({ ...e, [carga.key]: !isOpen }))}
                  className="w-full grid grid-cols-12 gap-2 px-6 py-4 hover:bg-[#1C1D24] text-left items-center"
                >
                  <div className="col-span-1">
                    {isOpen ? <ChevronDownIcon className="w-5 h-5 text-[#34C759]" /> : <ChevronRightIcon className="w-5 h-5 text-muted-foreground" />}
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Placa</div>
                    <div className="font-mono text-lg font-bold text-[#FFD60A]">{carga.placa}</div>
                    <Badge className="mt-1 rounded-none bg-[#34C759] text-black text-[9px] tracking-widest font-bold h-4 px-1.5">CONCLUÍDO</Badge>
                  </div>
                  <div className="col-span-3">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Motorista</div>
                    <div className="text-sm">{carga.motorista}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{carga.cpf_motorista}</div>
                  </div>
                  <div className="col-span-3">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cliente</div>
                    <div className="text-sm">{carga.cliente}</div>
                    <div className="text-[10px] font-mono text-[#00E5FF]">Pedido {carga.no_pedido} · NF {carga.nf_produto}</div>
                  </div>
                  <div className="col-span-1 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Contentores</div>
                    <div className="font-display text-2xl font-bold text-[#34C759]">{carga.contentores.length}</div>
                  </div>
                  <div className="col-span-2 text-right">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Entrega em</div>
                    <div className="text-xs font-mono text-[#34C759]">{carga.ultima_entrega ? new Date(carga.ultima_entrega).toLocaleString("pt-BR") : "—"}</div>
                  </div>
                </button>
                {isOpen && (
                  <div className="bg-[#0A0A0C] border-t border-border/60 px-6 py-3">
                    <div className="flex justify-end mb-2 gap-3">
                      {canEditCarga && (
                        <button
                          onClick={() => setEditCarga(carga)}
                          data-testid={`editar-carga-hist-${carga.placa}`}
                          className="text-[10px] uppercase tracking-widest text-[#FFD60A] hover:underline flex items-center gap-1"
                          title="Corrigir dados da carga (cliente, pedido, NF, motorista, placa)"
                        >
                          <EditIcon className="w-3 h-3" /> Editar carga
                        </button>
                      )}
                      <a
                        href={`/romaneio/expedicao/${encodeURIComponent(carga.placa)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-[10px] uppercase tracking-widest text-[#FFD60A] hover:underline flex items-center gap-1"
                      >
                        <PrinterIcon className="w-3 h-3" /> Imprimir Romaneio
                      </a>
                    </div>
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        <tr>
                          <th className="text-left py-2">N° Série</th>
                          <th className="text-left py-2">Tipo</th>
                          <th className="text-left py-2">Lote</th>
                          <th className="text-right py-2">Litragem</th>
                          <th className="text-left py-2">GPS</th>
                          <th className="text-right py-2">Entrega</th>
                        </tr>
                      </thead>
                      <tbody>
                        {carga.contentores.map((c) => (
                          <tr key={c.no_serie} className="border-t border-border/30">
                            <td className="py-2 font-mono text-[#FFD60A]">{c.no_serie}</td>
                            <td className="py-2">{c.tipo_armazenamento} · {c.capacidade}L</td>
                            <td className="py-2 font-mono">{c.no_lote}</td>
                            <td className="py-2 text-right font-mono">{c.qtd_envasado} L</td>
                            <td className="py-2 font-mono text-[#00E5FF]">{c.gps_id}</td>
                            <td className="py-2 text-right font-mono text-[#34C759]">{c.data_entrega_cliente ? new Date(c.data_entrega_cliente).toLocaleString("pt-BR") : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Modal: Cadastro rápido de cliente */}
      <Dialog open={openCliModal} onOpenChange={setOpenCliModal}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-2xl" data-testid="exp-cliente-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Cadastro rápido de cliente</DialogTitle>
            <div className="text-xs text-muted-foreground mt-1">
              Os dados de motorista/placa permanecem preservados. Ao salvar, o cliente é selecionado automaticamente.
            </div>
          </DialogHeader>
          <ClienteForm
            compact
            defaultTipo="destino"
            onCreated={async (novo) => {
              await loadClientes();
              setForm((f) => ({ ...f, cliente: novo.nome }));
              setOpenCliModal(false);
            }}
          />
        </DialogContent>
      </Dialog>

      <CargaEditModal
        open={!!editCarga}
        carga={editCarga}
        clientes={clientes}
        canRevert={true}
        onClose={() => setEditCarga(null)}
        onSaved={() => load()}
      />
    </div>
  );
}

function Field({ label, testId, value, onChange, mono, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <Input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className={`mt-1 rounded-none bg-[#0A0A0C] h-11 ${mono ? "font-mono" : ""}`}
      />
    </div>
  );
}
